/**
 * useCoordinateSystem — Track 3 Phase 7
 *
 * Owns the persistent coordinate-system preference ("cad" vs "svg")
 * extracted from `App.tsx` as part of the final residual cleanup
 * documented in `docs/plans/2026-05-08_track3_app_decomposition.md`
 * row 7 ("Cleanup: ribbon / context menu / inspector / coord-system").
 *
 * Responsibilities:
 *   - Read the initial value from `localStorage` under
 *     `editor.coordinateSystem` (matches the legacy key the previous
 *     inline implementation wrote to, so existing user preferences
 *     survive the extraction).
 *   - Mirror every change back to `localStorage` so the preference
 *     persists across sessions.
 *
 * Returned shape:
 *   { coordinateSystem, setCoordinateSystem, toggle }
 *
 * AutoCAD parity:
 *   The native AutoCAD UCS toggle lives in the status bar; this is the
 *   browser-editor analogue. "cad" is the default (Y up, matches DXF +
 *   AutoCAD); "svg" is the legacy Y-down option for screenshot diffing
 *   against the original SVG renderer.
 */

import { useCallback, useEffect, useState } from "react";

import type { CoordinateSystem } from "../scene3d/Scene3DStageProps";

const STORAGE_KEY = "editor.coordinateSystem";

export interface UseCoordinateSystemResult {
  coordinateSystem: CoordinateSystem;
  setCoordinateSystem: React.Dispatch<React.SetStateAction<CoordinateSystem>>;
  /** AutoCAD-style toggle (cad ⇄ svg). */
  toggle: () => void;
}

export function useCoordinateSystem(): UseCoordinateSystemResult {
  const [coordinateSystem, setCoordinateSystem] = useState<CoordinateSystem>(
    () => {
      if (typeof window === "undefined") return "cad";
      const stored = window.localStorage.getItem(STORAGE_KEY);
      return stored === "svg" || stored === "cad" ? stored : "cad";
    },
  );

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, coordinateSystem);
    }
  }, [coordinateSystem]);

  const toggle = useCallback(() => {
    setCoordinateSystem((prev) => (prev === "cad" ? "svg" : "cad"));
  }, []);

  return { coordinateSystem, setCoordinateSystem, toggle };
}
